/* eslint-disable */
import React, { useState } from "react";
import { useHistory, Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { authApi } from "../apis/authApi";
import logo from "../../src/assets/img/EF-logo-dark.png";
const emailrgx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const schema = yup.object({
  email: yup
    .string()
    .matches(emailrgx, "Invalid email")
    .required("Email is required")
    .trim(),
  password: yup
    .string()
    .min(3, "Minimum 3 characters")
    .required("Password is required")
    .trim(),
});

// ─── Permission helper functions ────────────────────────────────────────────
const hasPermission = (userPermissions, permissionName) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;

  return userPermissions.some((p) => {
    if (typeof p === "string") {
      return p === permissionName;
    }
    return p.name === permissionName;
  });
};

const hasAnyPermission = (userPermissions, permissions) => {
  if (!userPermissions) return false;
  return permissions.some((permission) =>
    hasPermission(userPermissions, permission),
  );
};

// ─── Get the first available menu item based on user permissions ───────────
const getFirstAvailablePath = (userPermissions) => {
  // Define all possible menu items in order of priority
  const menuItems = [
    // Dashboard & Reports
    { path: "/dashboard", permission: "reports.view" },
    { path: "/reports", permission: "reports.view" },

    // Roles & Permissions
    { path: "/roles", permission: "roles.view" },
    { path: "/roles/add", permission: "roles.create" },

    // Admin Users
    { path: "/users/admins", permission: "users.view" },
    { path: "/users/add-admin", permission: "users.create" },

    // Customer Users
    { path: "/users/customers", permission: "users.view" },
    { path: "/users/add-customer", permission: "users.create" },

    // Boat & Decks
    { path: "/boat/decks", permission: "boat.view" },
    { path: "/boat/decks/add", permission: "boat.create" },

    // Trips
    { path: "/trips", permission: "trips.view" },
    { path: "/trips/add", permission: "trips.create" },

    // Pricing
    { path: "/pricing", permission: "pricing.view" },

    // Reservations
    { path: "/reservations", permission: "reservations.view" },
    { path: "/reservations/add", permission: "reservations.create" },

    // Payments
    { path: "/payments", permission: "payments.view" },

    // Tickets
    { path: "/tickets", permission: "tickets.view" },
    { path: "/tickets/scan", permission: "tickets.check_in" },
  ];

  // Find the first menu item the user has permission for
  const availableItem = menuItems.find((item) =>
    hasPermission(userPermissions, item.permission),
  );

  if (availableItem) {
    console.log("First available path:", availableItem.path);
    return availableItem.path;
  }

  // If no dashboard permissions found, default to a safe fallback
  // This could be a "no access" page or the customer view
  console.log("No permissions found, redirecting to login?");
  return "/reservations/my"; // Fallback for customers
};

// ─── Role detection ────────────────────────────────────────────────────────────
const resolveIsAdmin = (user) => {
  if (!user) return false;
  const raw = user.role;
  if (!raw) return false;
  const name = (typeof raw === "string" ? raw : raw?.name || "").toLowerCase();
  return name === "admin";
};

// ─────────────────────────────────────────────────────────────────────────────
const Login = () => {
  const [eye, setEye] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  // 2FA challenge state
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaMethod, setMfaMethod] = useState("totp");
  const [mfaCode, setMfaCode] = useState("");
  const history = useHistory();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  // Persist auth state + redirect based on permissions.
  const finishLogin = (response) => {
    let permissions = [];
    if (
      response.user.permissions &&
      Array.isArray(response.user.permissions)
    ) {
      permissions = response.user.permissions.map((p) =>
        typeof p === "string" ? p : p.name,
      );
    } else if (response.user.role && response.user.role.permissions) {
      permissions = response.user.role.permissions.map((p) =>
        typeof p === "string" ? p : p.name,
      );
    }

    const userWithPermissions = { ...response.user, permissions };
    localStorage.setItem("access_token", response.accessToken);
    localStorage.setItem("user", JSON.stringify(userWithPermissions));
    history.replace(getFirstAvailablePath(permissions));
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setLoginError("");

      const response = await authApi.login({
        email: data.email,
        password: data.password,
      });

      // Two-factor enabled: switch to the code-entry step.
      if (response.mfaRequired) {
        setMfaToken(response.mfaToken);
        setMfaMethod(response.method || "totp");
        setMfaCode("");
        return;
      }

      finishLogin(response);
    } catch (error) {
      console.error("Login error:", error);
      setLoginError(error.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit2FA = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setLoginError("");
      const response = await authApi.verifyTwoFactor(mfaToken, mfaCode.trim());
      finishLogin(response);
    } catch (error) {
      setLoginError(error.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  const cancel2FA = () => {
    setMfaToken(null);
    setMfaCode("");
    setLoginError("");
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { width: 100%; height: 100%; overflow-x: hidden; }

        .crm-root {
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh; width: 100%;
          display: flex; align-items: stretch;
          background: #f8f7f2;
        }

        /* ── Left panel with warm brown gradient (matching Sidebar) ── */
        .crm-left {
          display: none; position: relative; overflow: hidden;
          background: linear-gradient(150deg, #5a3a22 0%, #7B4D12 50%, #8B5E1A 100%);
          align-items: center; justify-content: center; flex-direction: column;
          padding: 60px 52px; flex: 1;
        }
        @media (min-width: 900px) { .crm-left { display: flex; } }

        .crm-left-ring { position: absolute; border-radius: 50%; border: 1px solid rgba(255,255,255,0.09); pointer-events: none; }
        .crm-left-ring-a { width: 520px; height: 520px; top: -140px; left: -140px; background: rgba(255,255,255,0.025); }
        .crm-left-ring-b { width: 360px; height: 360px; bottom: -80px; right: -80px; background: rgba(255,255,255,0.03); }
        .crm-left-ring-c { width: 200px; height: 200px; top: 40%; left: 60%; background: rgba(255,255,255,0.02); }
        .crm-left-glow {
          position: absolute; inset: 0; pointer-events: none;
          background:
            radial-gradient(ellipse 60% 40% at 20% 15%, rgba(255,255,255,0.07) 0%, transparent 60%),
            radial-gradient(ellipse 50% 50% at 85% 85%, rgba(90,58,34,0.5) 0%, transparent 60%);
        }
        .crm-left-content { position: relative; z-index: 2; text-align: center; color: #fff; max-width: 320px; }
        .crm-left-icon {
          width: 72px; height: 72px; margin: 0 auto 30px;
          background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.18);
          border-radius: 20px; display: flex; align-items: center; justify-content: center;
          backdrop-filter: blur(12px);
        }
        .crm-left-icon svg { width: 36px; height: 36px; stroke: #fff; fill: none; stroke-width: 1.6; }
        .crm-left-headline { font-family: 'Sora', sans-serif; font-size: 30px; font-weight: 700; line-height: 1.2; letter-spacing: -0.5px; margin-bottom: 14px; }
        .crm-left-desc { font-size: 14.5px; color: rgba(255,255,255,0.6); font-weight: 300; line-height: 1.7; }
        .crm-left-pills { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 40px; justify-content: center; }
        .crm-left-pill {
          background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.14);
          border-radius: 100px; padding: 7px 15px; font-size: 11.5px;
          color: rgba(255,255,255,0.78); font-weight: 500; letter-spacing: 0.4px;
          backdrop-filter: blur(8px);
        }

        /* ── Right panel ── */
        .crm-right {
          display: flex; align-items: center; justify-content: center;
          padding: 40px 24px; background: #f8f7f2; min-height: 100vh; flex: 1;
        }
        @media (min-width: 900px) { .crm-right { flex: 0 0 480px; max-width: 480px; padding: 60px 64px; min-height: auto; } }

        .crm-form-shell {
          width: 100%; max-width: 380px;
          animation: crm-rise 0.48s cubic-bezier(0.22,1,0.36,1) both;
        }
        @keyframes crm-rise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

        /* Logo with brown gradient - updated for image logo */
        .crm-logo-row { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
          margin-bottom: 44px;
          justify-content: center;
          flex-direction: column;
        }
        .crm-logo-icon {
          width: 200px;
          height: 100px;
          background: transparent;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .crm-logo-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .crm-logo-name {
          font-family: 'Sora', sans-serif;
          font-size: 22px;
          font-weight: 700;
          background: linear-gradient(135deg, #5a3a22, #7B4D12);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          letter-spacing: -0.3px;
          text-align: center;
        }

        /* Headings - warm dark brown */
        .crm-h1 { font-family: 'Sora', sans-serif; font-size: clamp(24px, 5vw, 30px); font-weight: 700; color: #2d2418; letter-spacing: -0.6px; line-height: 1.15; margin-bottom: 8px; }
        .crm-sub { font-size: 14px; color: #8b7355; margin-bottom: 36px; line-height: 1.5; }

        /* Error banner */
        .crm-error-banner {
          display: flex; align-items: flex-start; gap: 10px;
          background: #fff1f0; border: 1px solid #fcd9d4; border-left: 3px solid #c2410c;
          border-radius: 10px; padding: 12px 14px; margin-bottom: 24px;
          font-size: 13.5px; color: #9b4a1a; font-weight: 500;
          animation: crm-rise 0.25s ease both;
        }
        .crm-error-banner svg { flex-shrink: 0; margin-top: 1px; width: 15px; height: 15px; stroke: #c2410c; fill: none; stroke-width: 2; }

        /* Fields */
        .crm-field { margin-bottom: 18px; }
        .crm-label { display: block; font-size: 13px; font-weight: 600; color: #4a3724; margin-bottom: 7px; letter-spacing: 0.15px; }
        .crm-field-inner { position: relative; }
        .crm-input {
          width: 100%; height: 50px; padding: 0 16px;
          font-family: 'DM Sans', sans-serif; font-size: 15px; color: #2d2418;
          background: #ffffff; border: 1.5px solid #e6dacf; border-radius: 11px;
          outline: none; transition: border-color 0.2s, box-shadow 0.2s;
          -webkit-appearance: none; appearance: none;
        }
        .crm-input::placeholder { color: #bc9f7a; }
        .crm-input:focus { border-color: #7B4D12; box-shadow: 0 0 0 3.5px rgba(123,77,18,0.12); }
        .crm-input--pw { padding-right: 48px; }
        .crm-input--err { border-color: #c2410c !important; background: #fffaf8 !important; }
        .crm-eye {
          position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer; padding: 5px;
          display: flex; color: #9b7b5c; transition: color 0.18s; line-height: 1;
        }
        .crm-eye:hover { color: #7B4D12; }
        .crm-eye svg { width: 17px; height: 17px; stroke: currentColor; fill: none; stroke-width: 2; }
        .crm-field-err { display: block; margin-top: 5px; font-size: 12px; color: #c2410c; font-weight: 500; }

        /* Button - brown gradient (matches Sidebar badge and icons) */
        .crm-btn {
          display: flex; align-items: center; justify-content: center; gap: 9px;
          width: 100%; height: 50px; margin-top: 26px;
          background: linear-gradient(135deg, #7B4D12, #8B5E1A);
          color: #fff; border: none; border-radius: 11px;
          font-family: 'Sora', sans-serif; font-size: 14.5px; font-weight: 600; letter-spacing: 0.2px;
          cursor: pointer; transition: transform 0.15s, box-shadow 0.2s, opacity 0.2s;
          box-shadow: 0 5px 22px rgba(123,77,18,0.35); position: relative; overflow: hidden;
        }
        .crm-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 9px 30px rgba(123,77,18,0.45); }
        .crm-btn:disabled { opacity: 0.62; cursor: not-allowed; transform: none !important; }

        .crm-spinner {
          width: 17px; height: 17px;
          border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #fff;
          border-radius: 50%; animation: crm-spin 0.65s linear infinite; flex-shrink: 0;
        }
        @keyframes crm-spin { to { transform: rotate(360deg); } }

        /* Footer links - brown */
        .crm-footer-line {
          margin-top: 36px; padding-top: 22px; border-top: 1px solid #e9e0d4;
          text-align: center; font-size: 11.5px; color: #b59573; letter-spacing: 0.2px;
        }
        .crm-footer-line a {
          color: #7B4D12;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.2s;
        }
        .crm-footer-line a:hover {
          color: #5a3a22;
          text-decoration: underline;
        }
      `}</style>

      <div className="crm-root">
        {/* ── Left decorative panel with brown gradient ── */}
        <div className="crm-left">
          <div className="crm-left-ring crm-left-ring-a" />
          <div className="crm-left-ring crm-left-ring-b" />
          <div className="crm-left-ring crm-left-ring-c" />
          <div className="crm-left-glow" />
          <div className="crm-left-content">
            <div className="crm-left-icon">
              <svg viewBox="0 0 24 24">
                <path d="M3 17l4-8 4 4 4-6 4 10" />
                <path d="M21 19H3" />
              </svg>
            </div>
            <div className="crm-left-headline">
             EGYPT FERRY
            </div>
            <p className="crm-left-desc">
              Complete reservation management for trips, decks, bookings,
              tickets, and revenue reports.
            </p>
            <div className="crm-left-pills">
              <span className="crm-left-pill">Reservations</span>
              <span className="crm-left-pill">QR Tickets</span>
              <span className="crm-left-pill">Revenue Reports</span>
              <span className="crm-left-pill">Seat Availability</span>
            </div>
          </div>
        </div>

        {/* ── Right form panel with brown theme ── */}
        <div className="crm-right">
          <div className="crm-form-shell">
            <div className="crm-logo-row">
              <div className="crm-logo-icon">
                <img src={logo} alt="EF Logo" className="crm-logo-img" />
              </div>
              {/* <span className="crm-logo-name">Boat Cruise Management</span> */}
            </div>

            <h1 className="crm-h1">
              {mfaToken ? "Two-factor authentication" : "Welcome back"}
            </h1>
            <p className="crm-sub">
              {mfaToken
                ? mfaMethod === "email"
                  ? "Enter the code we emailed you."
                  : "Enter the 6-digit code from your authenticator app."
                : "Sign in to your account"}
            </p>

            {loginError && (
              <div className="crm-error-banner" role="alert">
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {loginError}
              </div>
            )}

            {!mfaToken && (
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="crm-field">
                <label className="crm-label" htmlFor="crm-email">
                  Email Address
                </label>
                <div className="crm-field-inner">
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="crm-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@boatcruise.com"
                        disabled={loading}
                        className={`crm-input${
                          errors?.email ? " crm-input--err" : ""
                        }`}
                        {...field}
                      />
                    )}
                  />
                </div>
                {errors?.email && (
                  <small className="crm-field-err">
                    {errors.email.message}
                  </small>
                )}
              </div>

              <div className="crm-field">
                <label className="crm-label" htmlFor="crm-pw">
                  Password
                </label>
                <div className="crm-field-inner">
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <>
                        <input
                          id="crm-pw"
                          type={eye ? "password" : "text"}
                          autoComplete="current-password"
                          placeholder="Enter your password"
                          disabled={loading}
                          className={`crm-input crm-input--pw${
                            errors?.password ? " crm-input--err" : ""
                          }`}
                          {...field}
                        />
                        <button
                          type="button"
                          className="crm-eye"
                          onClick={() => setEye(!eye)}
                          aria-label={eye ? "Show password" : "Hide password"}
                        >
                          {eye ? (
                            <svg viewBox="0 0 24 24">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </>
                    )}
                  />
                </div>
                {errors?.password && (
                  <small className="crm-field-err">
                    {errors.password.message}
                  </small>
                )}
              </div>

              <button type="submit" className="crm-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="crm-spinner" />
                    Signing in…
                  </>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
            )}

            {mfaToken && (
              <form onSubmit={onSubmit2FA} noValidate>
                <div className="crm-field">
                  <label className="crm-label" htmlFor="crm-2fa">
                    Authentication code
                  </label>
                  <div className="crm-field-inner">
                    <input
                      id="crm-2fa"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      placeholder="123456"
                      disabled={loading}
                      className="crm-input"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                    />
                  </div>
                  <small className="crm-field-err" style={{ color: "#8b7355" }}>
                    You can also use one of your recovery codes.
                  </small>
                </div>

                <button
                  type="submit"
                  className="crm-btn"
                  disabled={loading || !mfaCode.trim()}
                >
                  {loading ? (
                    <>
                      <span className="crm-spinner" />
                      Verifying…
                    </>
                  ) : (
                    "Verify"
                  )}
                </button>

                <button
                  type="button"
                  onClick={cancel2FA}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#7B4D12",
                    fontWeight: 600,
                    cursor: "pointer",
                    marginTop: 16,
                    width: "100%",
                    fontSize: 13.5,
                  }}
                >
                  ← Back to login
                </button>
              </form>
            )}

            <div className="crm-footer-line">
              © 2026 Boat Cruise. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
